/**
 * NavigationButtons
 *
 * Step navigation, submit handling, and validation; hides on intro/success and
 * when survey is full on intro.
 */
import React from 'react';
import { ChevronLeft, ChevronRight, Upload, AlertCircle } from 'lucide-react';
import { ButtonSpinner } from '../../../components/ui/feedback/loading-spinner.jsx';

export function NavigationButtons({
  currentStep, 
  isUploading, 
  uploadProgress,
  goToPreviousStep, 
  goToNextStep,
  handleSubmit,
  theme, 
  themes,
  formData,
  videoFile,
  videoDuration,
  repsLoaded,
  customTheme,
  getTextStyle,
  getSubtextStyle,
  surveyFull = false,
  error = null
}) {
  // Log when component renders with surveyFull true
  React.useEffect(() => {
    if (surveyFull) {
      console.log('NavigationButtons: Survey is full, hiding buttons on intro page');
    }
  }, [surveyFull]);

  // Don't show navigation buttons on intro page if survey is full
  if (currentStep === 'intro' && surveyFull) {
    return null;
  }
  
  // Don't show navigation buttons on intro or success pages
  if (currentStep === 'intro' || currentStep === 'success') {
    return null;
  }

  const isStepValid = () => {
    // For response step, check if video is valid (exists and within duration limits)
    if (currentStep === 'response') {
      if (!videoFile) return false;
      if (videoDuration > 120) return false; // Over 2 minutes
      if (videoDuration < 10) return false; // Under 10 seconds
      return true;
    }

    switch (currentStep) {
      case 'contact':
        // Add email validation
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return Boolean(
          formData.firstName?.trim() && 
          formData.lastName?.trim() && 
          formData.email?.trim() && 
          emailRegex.test(formData.email)
        );
      case 'location':
        return Boolean(formData.zipCode?.match(/^\d{5}$/));
      default:
        return true;
    }
  };

  // Check if the current step is valid (has all required fields filled)
  const isValid = isStepValid();

  return (
    <div className="mt-8 space-y-6">
      {error && (
        <div className={`rounded-lg border ${
          theme 
            ? theme === 'custom'
              ? 'border-white/20 bg-black/20'
              : 'border-white/20 bg-black/20'
            : 'border-red-200 bg-red-50 dark:border-red-900/50 dark:bg-red-900/50'
        } p-4`}>
          <div className="flex items-start">
            <div className={`mr-3 flex-shrink-0 ${
              theme ? 'text-white' : 'text-red-600 dark:text-red-400'
            }`}>
              <AlertCircle className="h-5 w-5" />
            </div>
            <div>
              <h3 className={`text-sm font-medium ${
                theme ? 'text-white' : 'text-red-800 dark:text-red-200'
              }`} style={theme === 'custom' && getTextStyle ? getTextStyle() : {}}>Error</h3>
              <div className={`mt-2 text-sm ${
                theme ? 'text-white/80' : 'text-red-700 dark:text-red-300'
              }`} style={theme === 'custom' && getSubtextStyle ? getSubtextStyle() : {}}>
                <p>{error}</p>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="flex justify-between">
        <button
          type="button"
          onClick={goToPreviousStep}
          disabled={isUploading}
          className={`inline-flex items-center gap-2 rounded-lg px-4 py-2.5 text-sm font-medium ${
            theme 
              ? theme === 'custom'
                ? 'border-white/20 border-2 text-white hover:bg-white/10'
                : `${themes[theme]?.border || 'border-white/20'} border-2 ${themes[theme]?.text || 'text-white'} hover:bg-white/10` 
              : 'border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-300 dark:hover:bg-gray-800'
          } ${isUploading ? 'opacity-50 cursor-not-allowed' : ''}`}
          style={theme === 'custom' && getTextStyle ? getTextStyle() : {}}
        >
          <ChevronLeft className="h-4 w-4" />
          Back
        </button>
        {currentStep === 'response' ? (
          <button
            type="button"
            onClick={handleSubmit}
            disabled={isUploading || !isValid}
            className={`inline-flex items-center gap-2 rounded-lg px-4 py-2.5 text-sm font-medium ${
              theme 
                ? theme === 'custom'
                  ? 'border-white/20 border-2 text-white hover:bg-white/10'
                  : `${themes[theme]?.border || 'border-white/20'} border-2 ${themes[theme]?.text || 'text-white'} hover:bg-white/10` 
                : 'bg-primary-600 text-white hover:bg-primary-700'
            } ${(isUploading || !isValid) ? 'opacity-50 cursor-not-allowed' : ''}`}
            style={theme === 'custom' && getTextStyle ? getTextStyle() : {}}
          >
            {isUploading ? (
              <span className="flex items-center gap-2">
                <ButtonSpinner color="white" />
                Uploading...
              </span>
            ) : (
              <>
                <Upload className="h-4 w-4" />
                Submit Video
              </>
            )}
          </button>
        ) : (
          <button
            type="button"
            onClick={goToNextStep}
            disabled={isUploading || !isValid}
            className={`inline-flex items-center gap-2 rounded-lg px-4 py-2.5 text-sm font-medium ${
              theme 
                ? theme === 'custom'
                  ? 'border-white/20 border-2 text-white hover:bg-white/10'
                  : `${themes[theme]?.border || 'border-white/20'} border-2 ${themes[theme]?.text || 'text-white'} hover:bg-white/10` 
                : 'bg-primary-600 text-white hover:bg-primary-700'
            } ${(isUploading || !isValid) ? 'opacity-50 cursor-not-allowed' : ''}`}
            style={theme === 'custom' && getTextStyle ? getTextStyle() : {}}
          >
            Continue
            <ChevronRight className="h-4 w-4" />
          </button>
        )}
      </div>
    </div>
  );
}